import { useEffect, useState, useImperativeHandle } from "react";
import AddIcon from "@mui/icons-material/Add";
import PetsIcon from "@mui/icons-material/Pets";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RepeatIcon from "@mui/icons-material/Repeat";
import TimerIcon from "@mui/icons-material/Timer";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  useMediaQuery,
  useTheme,
  Grid,
  InputAdornment,
  Drawer,
} from "@mui/material";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import { FiCalendar, FiClock, FiArrowLeft } from "react-icons/fi";
import { MdCurrencyRupee, MdPets } from "react-icons/md";
import React from "react";
// import AddPetForm from "./AddPetForm";
import PetCard from "./PetCard";
import NotesIcon from "@mui/icons-material/Notes";
import { parse, isBefore } from "date-fns";
import AddNewPet from "./AddNewPet";
import { useAllowPastBooking, isBeforeMinBookingDate, pastBookingBlockedMessage } from "../../hooks/useAllowPastBooking";

// ReadOnly Display Component (same as in BoardingDrawer)
const ReadOnlyDisplay = ({
  label,
  value,
  placeholder,
  bgColor = "#f9fafb",
  borderColor = "#e5e7eb",
  textColor = "#6b7280",
}) => (
  <Box>
    <Typography
      variant="subtitle2"
      fontWeight="bold"
      mb={0.5}
      className="flex items-center gap-2"
    >
      {label}
    </Typography>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1.5,
        borderRadius: "12px",
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        minHeight: "40px",
        position: "relative",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: value ? "#374151" : textColor,
          fontStyle: value ? "normal" : "italic",
          flex: 1,
        }}
      >
        {value || placeholder}
      </Typography>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          right: "8px",
          transform: "translateY(-50%)",
          fontSize: "12px",
          color: "#9ca3af",
          fontWeight: 500,
          backgroundColor: bgColor,
          px: 1,
          borderRadius: "4px",
        }}
      >
        Auto
      </Box>
    </Box>
  </Box>
);

const DaycareInlineForm = React.forwardRef(
  (
    {
      isOpen,
      onClose,
      onUpdate,
      clientId,
      itemData,
      orderId,
      fromCalendar = false,
      selectedSlotInfo,
      editMode = false,
      onRefreshData = null,
      appointmentStatusRef = null,
      serviceTypeId = null,
      onPetFormStateChange = null,
      autoOpenPetForm = false,
      renderAsContent = false,
      suppressToasts = false, // New prop to control toast messages
      isPetDetails = false,
    },
    ref
  ) => {
    const { minDate, minDateStr, allowPastBooking, maxPastDays } = useAllowPastBooking();
    const [roomsDetails, setRoomsDetails] = useState([]);
    const [pets, setPets] = useState([]);
    const [services, setServices] = useState([]);
    const [allServices, setAllServices] = useState([]); // Store all services for filtering
    // Daycare-specific pricing state derived from API
    const [daycarePricing, setDaycarePricing] = useState({});
    const [durationOptions, setDurationOptions] = useState([]); // [{ key: '1hr', label: '1 Hr' }]
    const [frequencyOptions, setFrequencyOptions] = useState([]); // [{ key: 'one_time', label: 'One-time' }]

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [selectedPetId, setSelectedPetId] = useState("");
    const [isPetDisabled, setIsPetDisabled] = useState(false);
    const [showNewPetForm, setShowNewPetForm] = useState(
      autoOpenPetForm || false
    );
    const [isEditPet, setIsEditPet] = useState(false);
    const [newPet, setNewPet] = useState({
      type: "",
      name: "",
      gender: "",
      size: "",
      breed: "",
      age: "",
      birth_date: "",
      notes: "",
      tags: [],
      weight_kg: "",
      behavior_notes: "",
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isRoomOccupied, setIsRoomOccupied] = useState(false);

    const [petService, setPetService] = useState({
      pet_id: "",
      checkin_date: "",
      start_time: "",
      frequency: "",
      duration: "",
      service_id: "",
      roomtype_id: "",
      room: "",
      price: 0,
      discount: 0,
      gst: 0,
      tax_percentage: 0,
      gst_amount: 0,
      totalAmount: 0,
      // Service details
      service_name: "",
      service_category_name: "",
      service_type_name: "",
      service_details: null,
      notes: "",
    });

    const [editDataFetched, setEditDataFetched] = useState(false);

    // Helper to determine if a time slot is in the past relative to now
    const isPastTimeSlot = (slotLabel, selectedDate) => {
      if (!selectedDate) return false; // No date selected yet
      try {
        const slotDate = parse(
          `${selectedDate} ${slotLabel}`,
          "yyyy-MM-dd h:mm a",
          new Date()
        );
        return isBefore(slotDate, new Date());
      } catch {
        return false;
      }
    };
    // Handle enquiry edit mode
    useEffect(() => {
      if (itemData?._fromEnquiry && !editDataFetched) {
        const handleEnquiryEdit = async () => {
          try {
            // Call the API to get enquiry data
            const enquiryResponse = await httpService.get(
              `/vendorservice/getvendorservice/6870ef1cdca6041da36af69f`
            );

            // Use the enquiry data from the API response or fallback to itemData
            const enquiryData = enquiryResponse.data || itemData._enquiryData;

            if (
              enquiryData &&
              enquiryData.items &&
              enquiryData.items.length > 0
            ) {
              const firstItem = enquiryData.items[0];

              // Fetch pet details
              if (firstItem.vendor_pet_id) {
                await fetchPetDetails(firstItem.vendor_pet_id);
                setSelectedPetId(firstItem.vendor_pet_id);
              }
              // Fetch service details if available
              if (firstItem.service_id) {
                await fetchServiceDetails(firstItem.service_id);
              }

              // Parse date for daycare (checkin date)
              const checkinDate =
                firstItem.start_date || firstItem.appointment_date
                  ? new Date(firstItem.start_date || firstItem.appointment_date)
                      .toISOString()
                      .split("T")[0]
                  : "";

              setPetService((prev) => ({
                ...prev,
                pet_id: firstItem.vendor_pet_id || "",
                checkin_date: checkinDate,
                start_time: firstItem.time_slot || firstItem.start_time || "",
                frequency: firstItem.frequency || "One-time",
                duration: firstItem.duration || "",
                service_id: firstItem.service_id || "",
                roomtype_id: firstItem.room_type_id || "",
                room: firstItem.room_id || "",
                price: firstItem.price || firstItem.unit_price || 0,
                discount: firstItem.discount || 0,
                gst: firstItem.gst || 0,
                tax_percentage: firstItem.tax_percentage || 0, // Set tax_percentage to match gst for consistency
                totalAmount: firstItem.total_price || firstItem.price || 0,
                notes: firstItem?.notes,
              }));

              setEditDataFetched(true);
            }
          } catch (error) {
            console.error("Error fetching enquiry data:", error);
            toast.error("Failed to load enquiry data for editing");
          }
        };

        handleEnquiryEdit();
      }
    }, [itemData?._fromEnquiry, editDataFetched]);

    useEffect(() => {
      if (isPetDetails && (itemData?.pet_id || itemData?.vendor_pet_id)) {
        setIsPetDisabled(true);
        if (!petService.pet_id && (itemData.pet_id || itemData.vendor_pet_id)) {
          setPetService(prev => ({
            ...prev,
            pet_id: itemData.pet_id || itemData.vendor_pet_id
          }));
          fetchPetDetails(itemData.pet_id || itemData.vendor_pet_id);
        }
      }
    }, [isPetDetails, itemData, petService.pet_id]);

    // Add this new useEffect after the existing useEffect that loads pets (around line 450):

    // Try to match pet by name when pets are loaded and we only have pet_name
    useEffect(() => {
      if (pets.length > 0 && !petService.pet_id && petService.pet_name) {
        const foundPet = pets.find((pet) => pet.name === petService.pet_name);
        if (foundPet) {
          setPetService((prev) => ({
            ...prev,
            pet_id: foundPet._id,
          }));
          fetchPetDetails(foundPet._id);
          setSelectedPetId(foundPet._id);
        } else {
          console.warn(
            "🔧 DaycareInlineForm - Could not find pet with name:",
            petService.pet_name
          );
        }
      }
    }, [pets, petService.pet_id, petService.pet_name]);

    useEffect(() => {
      const init = async () => {
        if (
          itemData?._id &&
          !fromCalendar &&
          !editDataFetched &&
          !itemData._fromEnquiry
        ) {
          // set form Data from itemData
          const petId = itemData.vendor_pet_id || itemData.pet_id;
          if (petId) {
            await fetchPetDetails(petId);
            setSelectedPetId(petId);
          }
          if (itemData.service_id) {
            await fetchServiceDetails(itemData.service_id);
          }

          // Enhanced data mapping to handle vendorbooking API response
          const tempFormData = {
            pet_id: petId,
            checkin_date: itemData.start_date
              ? new Date(itemData.start_date).toISOString().split("T")[0]
              : "",
            start_time: itemData.start_time || "",
            frequency: itemData.frequency || "One-time",
            duration: itemData.duration || "",
            service_id: itemData.service_id || "",
            roomtype_id: itemData.room_type_id || "",
            room: itemData.room_id || itemData.room || "",
            price: itemData.price || itemData.unit_price || 0,
            discount: itemData.discount || 0,
            gst: itemData.gst || 0,
            tax_percentage: itemData.tax_percentage || 0, // Set tax_percentage to match gst for consistency
            totalAmount: itemData.total_price || 0,
            // Additional fields from vendorbooking API response
            service_name: itemData.service_name || "",
            service_code: itemData.service_code || "",
            client_name: itemData.client_name || "",
            pet_name: itemData.pet_name || "",
            quantity: itemData.quantity || 1,
            addons: itemData.addons || [],
            type: itemData.type || "day_care",
            notes: itemData?.notes || "",
            unit_price: itemData?.unit_price,
          };
          setPetService(tempFormData);
          setEditDataFetched(true);
          // Replace your calendar edit mode section (around line 285) with this updated version:
        } else if (
          fromCalendar &&
          itemData &&
          (itemData.service_id ||
            itemData.pet_id ||
            itemData.vendor_pet_id ||
            itemData.pet_name)
        ) {
          // Handle calendar edit mode
          let petId = itemData.pet_id || itemData.vendor_pet_id;

          // If pet_id is undefined but we have pet_name, try to find the pet by name
          if (!petId && itemData.pet_name && pets.length > 0) {
            const foundPet = pets.find((pet) => pet.name === itemData.pet_name);
            if (foundPet) {
              petId = foundPet._id;
            }
          }

          if (petId) {
            await fetchPetDetails(petId);
            setSelectedPetId(petId);
          } else if (itemData.pet_name) {
            // We'll handle this in the UI by showing the pet name even without the full pet details
          }

          if (itemData.service_id) {
            await fetchServiceDetails(itemData.service_id);
          }

          const tempFormData = {
            pet_id: petId || "", // This might still be empty, but we'll handle it in the UI
            checkin_date:
              itemData.checkin_date ||
              (itemData.start_date
                ? new Date(itemData.start_date).toISOString().split("T")[0]
                : ""),
            start_time: itemData.start_time || "",
            frequency: itemData.frequency || "One-time",
            duration: itemData.duration || "",
            service_id: itemData.service_id || "",
            roomtype_id: itemData.room_type_id || "",
            room: itemData.room_id || itemData.room || "",
            price: itemData.price || itemData.unit_price || 0,
            discount: itemData.discount || 0,
            gst: itemData.gst || 0,
            tax_percentage: itemData.tax_percentage || 0,
            totalAmount: itemData.total_price || itemData.totalAmount || 0,
            service_name: itemData.service_name || "",
            service_code: itemData.service_code || "",
            client_name: itemData.client_name || "",
            pet_name: itemData.pet_name || "", // Store pet_name separately
            quantity: itemData.quantity || 1,
            addons: itemData.addons || [],
            type: itemData.type || "day_care",
            notes: itemData?.notes || "",
            unit_price: itemData?.unit_price,
          };

          setPetService(tempFormData);
          setEditDataFetched(true);
        } else if (
          itemData &&
          Object.keys(itemData).length > 0 &&
          !editDataFetched
        ) {
          // Handle other cases where itemData is provided but not from specific flows

          const petId = itemData.vendor_pet_id || itemData.pet_id;
          if (petId) {
            await fetchPetDetails(petId);
            setSelectedPetId(petId);
          }

          if (itemData.service_id) {
            await fetchServiceDetails(itemData.service_id);
          }

          const tempFormData = {
            pet_id: petId || "",
            checkin_date:
              itemData.checkin_date ||
              (itemData.start_date
                ? new Date(itemData.start_date).toISOString().split("T")[0]
                : ""),
            start_time: itemData.start_time || "",
            frequency: itemData.frequency || "One-time",
            duration: itemData.duration || "",
            service_id: itemData.service_id || "",
            roomtype_id: itemData.room_type_id || "",
            room: itemData.room_id || itemData.room || "",
            price: itemData.price || itemData.unit_price || 0,
            discount: itemData.discount || 0,
            gst: itemData.gst || 0,
            tax_percentage: itemData.tax_percentage || 0, // Set tax_percentage to match gst for consistency
            totalAmount: itemData.total_price || itemData.totalAmount || 0,
            // Additional fields from vendorbooking API response
            service_name: itemData.service_name || "",
            service_code: itemData.service_code || "",
            client_name: itemData.client_name || "",
            pet_name: itemData.pet_name || "",
            quantity: itemData.quantity || 1,
            addons: itemData.addons || [],
            type: itemData.type || "day_care",
            notes: itemData?.notes || "",
          };

          setPetService(tempFormData);
          setEditDataFetched(true);
        }
      };
      setIsLoading(true);
      init();
      setIsLoading(false);
    }, [itemData, fromCalendar, editDataFetched]);

    // New useEffect to call price API when service_id is set and service details are fetched
    useEffect(() => {
      const callPriceAPI = async () => {
        if (
          petService.service_id &&
          petService.frequency &&
          petService.duration
        ) {
          // Convert frequency label to key format for API
          const frequencyKey = mapFreqLabelToKey(petService.frequency);
          const durationKey = petService.duration;

          await fetchPriceFromAPI(
            petService.service_id,
            frequencyKey,
            durationKey
          );
        }
      };

      callPriceAPI();
    }, [petService.service_id, petService.frequency, petService.duration]);

    // Additional useEffect to handle service_id changes from vendorbooking API
    useEffect(() => {
      const handleServiceIdFromVendorBooking = async () => {
        // If service_id is set from vendorbooking API and we have frequency/duration, call price API
        if (
          petService.service_id &&
          petService.frequency &&
          petService.duration
        ) {
          // Convert frequency label to key format for API
          const frequencyKey = mapFreqLabelToKey(petService.frequency);
          const durationKey = petService.duration;

          await fetchPriceFromAPI(
            petService.service_id,
            frequencyKey,
            durationKey
          );
        }
      };

      handleServiceIdFromVendorBooking();
    }, [petService.service_id]);

    const [formData, setFormData] = useState({
      order_type: "offline",
      vendor_client_id: "",
      order_items: [
        {
          item_type: "day_care",
          vendor_pet_id: "",
          service_id: "",
          technician_id: "",
          start_date: "",
          start_time: "",
          duration: "",
          frequency: "",
          quantity: "",
          unit_price: 0,
          discount: 0,
          gst: 0,
          gst_amount: 0,
          total_price: 0,
          addons: [],
        },
      ],
    });

    // const [petTypes, setPetTypes] = useState([]);
    // const [petSizes, setPetSizes] = useState([]);
    // const [petBreeds, setPetBreeds] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [petDetails, setPetDetails] = useState("");
    const [rooms, setRooms] = useState([]);
    const [servicesd, setServicesd] = useState({});

    // const filteredBreeds = React.useMemo(() => {
    //   if (!newPet.type) return petBreeds;
    //   return petBreeds.filter((b) => b.pet_type_id === newPet.type);
    // }, [petBreeds, newPet.type]);

    useEffect(() => {
      const fetchInitialData = async () => {
        try {
          const [petsRes, typesRes, breedsRes] = await Promise.all([
            httpService.get(`/vendorpet/getvendorclientpets/${clientId}`),
            // httpService.get("/petTypes/getPetTypes"),
            // httpService.get("/petBreeds/getPetBreeds"),
          ]);
          setPets(petsRes.data);
          // setPetTypes(typesRes.data);
          // setPetBreeds(breedsRes.data);
        } catch (error) {
          console.error("Error loading initial data", error);
        }
      };
      fetchInitialData();
    }, [clientId, serviceTypeId]);

    useEffect(() => {
      const fetchServices = async () => {
        try {
          const petType = petDetails?.pet_type?.name;
          console.log("Fetching daycare services for pet type:", petType);
          
          // Get all active daycare services (don't filter by petType on backend)
          // const response = await httpService.get(
          //   `/vendordaycareservice/getvendordaycareservices?serviceCategoryTypeId=${
          //     serviceTypeId || null
          //   }&status=active`
          // );
          
          const response = await httpService.get(
            `/vendordaycareservice/getvendordaycareservices?status=active`
          );
          
          console.log("All daycare services from API:", response.data);
          
          // Filter services client-side based on pet type (handle array of objects format)
          const filteredServices = response.data.filter(service => {
            if (!service.pet_type) {
              console.warn(`Daycare service ${service.name} has no pet_type`);
              return false;
            }
            
            const currentPetType = petType?.toLowerCase()?.trim();
            if (!currentPetType) return false;
            
            // Handle different pet_type formats
            let servicePetTypes = [];
            
            if (Array.isArray(service.pet_type)) {
              // Array of objects format
              servicePetTypes = service.pet_type
                .map(pt => pt?.name?.toLowerCase()?.trim())
                .filter(name => name);
            } else if (typeof service.pet_type === 'string') {
              // String format (comma-separated)
              servicePetTypes = service.pet_type.toLowerCase().split(',').map(pt => pt.trim());
            } else {
              // Unknown format
              console.warn(`Daycare service ${service.name} has unknown pet_type format:`, service.pet_type);
              return false;
            }
            
            console.log(`Daycare service: ${service.name}, Pet types:`, servicePetTypes);
            
            // Check if "Both" or exact match
            return servicePetTypes.includes('both') || servicePetTypes.includes(currentPetType);
          });
          
          console.log("Filtered daycare services for", petType, ":", filteredServices);
          
          const dayCareServices = filteredServices;
          setServices(dayCareServices);
          setAllServices(dayCareServices);
          buildFrequencyOptionsFromAllServices(dayCareServices);
          
          if (!filteredServices.length && petType) {
            toast.warn(
              `No daycare services found for pet type "${petType}". Please add services for ${petType} or create services with "Both" pet type.`
            );
          }
        } catch (error) {
          console.error("Error fetching daycare services:", error);
        }
      };

      if (petDetails?._id) {
        fetchServices();
      }
    }, [petDetails?._id, serviceTypeId]);

    // Add this useEffect to auto-select the pet when pets data is loaded
    useEffect(() => {
      if (petService.pet_id && pets.length > 0) {
        // Check if the pet_id exists in the pets list
        const selectedPet = pets.find((pet) => pet._id === petService.pet_id);
        if (selectedPet) {
          // The dropdown should automatically show the selected pet
          // because petService.pet_id is already set
        } else {
          console.warn(
            "Selected pet not found in pets list:",
            petService.pet_id
          );
        }
      }
    }, [pets, petService.pet_id]);

    // useEffect(() => {
    //   const fetchSizes = async () => {
    //     if (!newPet.type) {
    //       setPetSizes([]);
    //       return;
    //     }
    //     const selectedType = petTypes.find((t) => t._id === newPet.type);
    //     if (selectedType?._id) {
    //       try {
    //         const res = await httpService.get(
    //           `/vendorpetsize/getVendorPetSizes?petTypeId=${selectedType._id}`
    //         );
    //         setPetSizes(res.data);
    //       } catch (error) {
    //         console.error("Error fetching pet sizes", error);
    //       }
    //     }
    //   };
    //   fetchSizes();
    // }, [newPet.type, petTypes]);

    useEffect(() => {
      const checkRoomOccupied = async () => {
        if (
          petService.checkin_date &&
          petService.room &&
          petService.roomtype_id
        ) {
          const payload = {
            room_id: petService.roomtype_id,
            room_number_id: petService.room,
            check_in: petService.checkin_date,
            type: "day_care",
            boarding_id: itemData?._id || null,
          };
          try {
            const response = await httpService.post(
              "/room/isRoomOccupied",
              payload
            );
            if (response.data.occupied) {
              setIsRoomOccupied(true);
              toast.error(
                "Room is already occupied for the selected date/time."
              );
            } else {
              setIsRoomOccupied(false);
              toast.success("Room is available for booking.");
            }
          } catch (error) {
            console.error("Error checking room occupancy:", error);
          }
        } else {
          setIsRoomOccupied(false);
        }
      };
      if (!isLoading) checkRoomOccupied(); // ✅ Call the async function
    }, [petService.checkin_date, petService.room, petService.roomtype_id]);

    const handlePetServiceChange = (field, value) => {
      if (field === "service_id") {
      }
      const now = new Date(); // Get current date and time
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      // Handle date validation for checkin_date
      if (field === "checkin_date") {
        const checkinDate = new Date(value);
        checkinDate.setHours(0, 0, 0, 0); // Normalize to start of day for comparison
        if (isBeforeMinBookingDate(checkinDate, minDate)) {
          toast.error(pastBookingBlockedMessage(allowPastBooking, maxPastDays, "Day care"));
          return;
        }
      }

      if (field === "start_time" && petService.checkin_date) {
        const selectedDate = new Date(petService.checkin_date);

        // Only check time if the selected date is today
        if (selectedDate.setHours(0, 0, 0, 0) === today.getTime()) {
          const [selectedHour, selectedMinute] = value.split(":").map(Number);

          const combinedSelectedDateTime = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            selectedHour,
            selectedMinute
          );

          if (combinedSelectedDateTime < now) {
            toast.error(
              "Check-in time cannot be in the past for today's date."
            );
            return;
          }
        }
      }

      setPetService((prev) => {
        const newState = {
          ...prev,
          [field]: value,
        };
        return newState;
      });
    };

    const resetFormData = () => {
      setPetDetails("");
      setServicesd({});
      setPetService({
        pet_id: "",
        checkin_date: "",
        start_time: "",
        frequency: "",
        duration: "",
        service_id: "",
        roomtype_id: "",
        room: "",
        price: 0,
        discount: 0,
        gst: 0,
        gst_amount: 0,
        totalAmount: 0,
        service_name: "",
        service_category_name: "",
        service_type_name: "",
        service_details: null,
        notes: "",
      });
      setFormData({
        order_type: "offline",
        vendor_client_id: clientId,
        order_items: [
          {
            item_type: "day_care",
            vendor_pet_id: "",
            service_id: "",
            technician_id: "",
            start_date: "",
            start_time: "",
            duration: "",
            frequency: "",
            quantity: "",
            unit_price: 0,
            discount: 0,
            gst: 0,
            gst_amount: 0,
            total_price: 0,
            addons: [],
          },
        ],
      });
    };

    // Expose methods to parent component through ref
    useImperativeHandle(ref, () => ({
      closePetForm: () => {
        setShowNewPetForm(false);
      },
      resetFormData: () => {
        resetFormData();
      },
    }));

    // Effect to notify parent about pet form state changes
    useEffect(() => {
      if (onPetFormStateChange) {
        onPetFormStateChange(showNewPetForm);
      }
    }, [showNewPetForm, onPetFormStateChange]);

    const generateTimeSlots = () => {
      const slots = [];
      let start = 8 * 60; // 8:00 AM in minutes
      const end = 20 * 60; // 8:00 PM in minutes

      while (start <= end) {
        const hours = Math.floor(start / 60);
        const minutes = start % 60;
        const formatted = new Date(0, 0, 0, hours, minutes).toLocaleTimeString(
          "en-IN",
          {
            hour: "numeric",
            minute: "2-digit",
          }
        );
        slots.push(formatted);

        start += 15;
      }

      return slots;
    };

    const timeSlots = generateTimeSlots();

    const fetchServiceDetails = async (serviceId) => {
      try {
        if (!serviceId) return;

        const response = await httpService.get(
          `/vendordaycareservice/getvendordaycareservice/${serviceId}`
        );

        const serviceData = response?.data?.data || response?.data;
        if (!serviceData) {
          console.error(
            "🔍 DaycareInlineForm - No service data found in response"
          );
          return;
        }

        // Extract pricing and build options
        const pricing = serviceData?.pricing || {};
        setDaycarePricing(pricing);
        const durationOpts = Object.keys(pricing).map((key) => ({
          key,
          label: key.replace("hr", " Hr"),
        }));
        setDurationOptions(durationOpts);

        // Build frequency options from selected service pricing
        const allFrequencies = new Set();
        Object.values(pricing).forEach((durationPricing) => {
          Object.keys(durationPricing).forEach((freqKey) => {
            allFrequencies.add(freqKey);
          });
        });

        const freqOptions = Array.from(allFrequencies).map((key) => ({
          key,
          label: mapFreqKeyToLabel(key),
        }));
        setFrequencyOptions(freqOptions);

        // Update GST and basic service meta
        setPetService((prev) => ({
          ...prev,
          service_name: serviceData?.name || "",
          service_category_name:
            serviceData?.service_category?.service_category_name || "",
          service_type_name:
            serviceData?.service_category?.service_type_name || "Daycare",
          service_details: serviceData,
          ...(prev.service_id !== serviceId && { service_id: serviceId }),
          // Don't reset duration/frequency/price here since they're set in the flow
          // Preserve GST from vendorbooking API response, only set from service if not already set
          tax_percentage: serviceData?.tax_percentage || 0,
        }));

        // Daycare service doesn't include addons in current contract
        setServicesd({});
      } catch (error) {
        console.error("Failed to fetch service details:", error);
        toast.error("Failed to fetch service details");
      }
    };

    const mapFreqKeyToLabel = (key) => {
      const map = {
        one_time: "One-time",
        weekly: "Weekly",
        monthly: "Monthly",
        quaterly: "Quarterly",
        quarterly: "Quarterly",
      };
      return map[key] || key;
    };

    const mapFreqLabelToKey = (label) => {
      if (!label) return "";

      const key = (label || "").toLowerCase().replace(/\s+/g, "_");
      if (key === "one-time" || key === "one_time" || key === "one-time")
        return "one_time";
      if (key === "quarterly" || key === "quaterly") return "quaterly";
      if (key === "weekly") return "weekly";
      if (key === "monthly") return "monthly";
      return key;
    };

    const buildFrequencyOptionsFromAllServices = (servicesArray) => {
      const allFrequencies = new Set();
      servicesArray.forEach((service) => {
        const pricing = service?.pricing || {};
        Object.values(pricing).forEach((durationPricing) => {
          Object.keys(durationPricing).forEach((freqKey) => {
            allFrequencies.add(freqKey);
          });
        });
      });

      const freqOptions = Array.from(allFrequencies).map((key) => ({
        key,
        label: mapFreqKeyToLabel(key),
      }));
      setFrequencyOptions(freqOptions);
    };

    const buildDurationOptionsFromFrequency = (selectedFrequencyKey) => {
      const availableDurations = new Set();
      allServices.forEach((service) => {
        const pricing = service?.pricing || {};
        Object.keys(pricing).forEach((durKey) => {
          if (pricing[durKey][selectedFrequencyKey] != null) {
            availableDurations.add(durKey);
          }
        });
      });

      const durOptions = Array.from(availableDurations).map((key) => ({
        key,
        label: key.replace("hr", " Hr"),
      }));
      setDurationOptions(durOptions);
    };

    const filterServicesBasedOnFrequencyAndDuration = (
      frequencyKey,
      durationKey
    ) => {
      const filteredServices = allServices.filter((service) => {
        const pricing = service?.pricing || {};
        return (
          pricing[durationKey] && pricing[durationKey][frequencyKey] != null
        );
      });
      setServices(filteredServices);
    };

    const fetchPriceFromAPI = async (serviceId, frequencyKey, durationKey) => {
      try {
        if (!serviceId || !frequencyKey || !durationKey) return;

        const payload = {
          vendor_service_id: serviceId,
          frequency: frequencyKey,
          duration: durationKey,
        };

        const response = await httpService.post(
          "/vendorbooking/getPriceCalculation?service_type=day_care",
          payload
        );

        // Check if response exists and handle different response structures
        if (response && response.data !== undefined) {
          // Handle different response structures
          let calculatedPrice = 0;
          let gstAmount = 0;
          if (typeof response.data === "number") {
            // Direct number response
            calculatedPrice = response.data;
          } else if (
            typeof response.data === "object" &&
            response.data !== null
          ) {
            // Object response - check for fields
            calculatedPrice = response.data.price || 0;
            gstAmount = response.data.taxAmount || 0;
          } else {
            // Fallback
            calculatedPrice = 0;
          }

          // Preserve existing discount from vendorbooking API response
          const currentDiscount = petService.discount || 0;

          setPetService((prev) => ({
            ...prev,
            price: calculatedPrice,
            gst_amount: gstAmount, // store GST separately if needed
            discount: currentDiscount, // preserve discount from vendorbooking API
          }));
        } else {
          console.error(
            "🔍 DaycareInlineForm - Invalid price API response:",
            response.data
          );
          toast.error("Failed to get price from API");
        }
      } catch (error) {
        console.error(
          "🔍 DaycareInlineForm - Error fetching price from API:",
          error
        );
        toast.error("Failed to fetch price from API");
      }
    };

    const recomputePriceFromSelection = (nextDurationKey, nextFrequencyKey) => {
      const durKey = nextDurationKey || petService.duration || "";
      const freqKey =
        nextFrequencyKey || mapFreqLabelToKey(petService.frequency || "");
      if (
        daycarePricing &&
        daycarePricing[durKey] &&
        daycarePricing[durKey][freqKey] != null
      ) {
        const base = daycarePricing[durKey][freqKey];
        setPetService((prev) => ({ ...prev, price: Number(base) }));
      }
    };

    const fetchRoomDetails = async (roomtype_id) => {
      try {
        const response = await httpService.get(
          `/vendorroom/getvendorrooms?roomType=${roomtype_id}`
        );
        setRooms(response.data);
        setRoomsDetails(response.data);
        return response.data;
      } catch (error) {
        console.error("Failed to fetch room details:", error);
        return null;
      }
    };

    // Calculate total amount
    useEffect(() => {
      const basePrice = parseFloat(petService.price || "0") || 0;
      const discount = parseFloat(petService.discount || "0") || 0;
      const gstPercentage = parseFloat(petService.tax_percentage || "0") || 0;

      const addonsTotal = Object.values(servicesd || {}).reduce(
        (sum, addon) => {
          const addonPrice = parseFloat(addon.price);
          if (addon.selected && !isNaN(addonPrice)) {
            return sum + addonPrice;
          }
          return sum;
        },
        0
      );

      // Calculate subtotal including addons
      const grossAmount = basePrice + addonsTotal;
      const subtotalRaw = grossAmount - discount;
      const subtotal = Math.max(0, subtotalRaw);
      const gstAmount = (grossAmount * gstPercentage) / 100;
      const total = Math.max(0, subtotal + gstAmount);

      setPetService((prev) => ({
        ...prev,
        totalAmount: isNaN(total) ? "0.00" : total.toFixed(2),
        gst_amount: gstAmount, // Update GST amount for consistency
      }));
    }, [
      petService.price,
      petService.discount,
      petService.tax_percentage,
      servicesd,
    ]);

    const fetchPetDetails = async (petId) => {
      try {
        const response = await httpService.get(
          `/vendorpet/getvendorpet/${petId}`
        );
        const petDetail = response.data;
        setPetDetails(petDetail);
      } catch (error) {
        console.error("Failed to fetch pet details:", error);
      }
    };

    const handleAddNewPet = async (newPet) => {
      const newPetObj = {
        vendor_client_id: clientId,
        name: newPet.name,
        pet_type: newPet.type,
        size: newPet.size,
        breed: newPet.breed,
        birth_date: newPet.birth_date,
        age: newPet.age,
        gender: newPet.gender,
        notes: newPet.notes,
        tags: newPet.tags,
        weight_kg: newPet.weight_kg,
        behavior_notes: newPet.behavior_notes,
      };
      const res = await httpService.post("/vendorpet/addvendorPet", newPetObj);
      if (!suppressToasts) {
        toast.success("Pet added successfully!", {
          position: "top-right",
        });
      }
      const newPetData = res.data;
      setPetService((prev) => ({
        ...prev,
        pet_id: newPetData._id,
        petDetails: newPetData,
      }));
      fetchPetDetails(newPetData._id);
      setPets((prevPets) => [...prevPets, newPetData]);
    };

    const handleNewPetSave = () => {
      handleAddNewPet(newPet);
      setShowNewPetForm(false);
      setNewPet({
        type: "",
        name: "",
        gender: "",
        size: "",
        breed: "",
        age: "",
        birth_date: "",
        notes: "",
        tags: [],
        weight_kg: "",
        behavior_notes: "",
      });
    };

    const handleAddonToggle = (key) => {
      setServicesd((prev) => {
        const updatedServices = {
          ...prev,
          [key]: {
            ...prev[key],
            selected: !prev[key].selected,
          },
        };

        // Extract selected addons from updatedServices
        // const selectedAddons = Object.entries(updatedServices)
        //   .filter(([_, addon]) => addon.selected)
        //   .map(([addonId, addon]) => ({
        //     addon_id: addonId,
        //     addon_price: Number(addon.price),
        //     name: addon.label,
        //   }));

        // Update formData with selected addons
        setFormData((prevForm) => ({
          ...prevForm,
          order_items: [
            {
              ...prevForm.order_items[0],
              // addons: selectedAddons,
            },
          ],
        }));

        return updatedServices;
      });
    };

    const handleServicePriceChange = (key, newPrice) => {
      setServicesd((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          price: Number(newPrice),
        },
      }));
    };

    const handleSaveOrders = async () => {
      try {
        // Validate required fields
        if (!petService?.pet_id) {
          toast.warn("Please select a pet", {
            position: "top-right",
          });
          return;
        }

        if (!petService?.frequency) {
          toast.warn("Please select frequency", {
            position: "top-right",
          });
          return;
        }

        if (!petService?.duration) {
          toast.warn("Please select duration", {
            position: "top-right",
          });
          return;
        }

        if (!petService?.service_id) {
          toast.warn("Please select a service", {
            position: "top-right",
          });
          return;
        }

        // Get selected addons
        // const selectedAddons = Object.entries(servicesd)
        //   .filter(([_, addon]) => addon.selected)
        //   .map(([addonId, addon]) => ({
        //     addon_id: addonId,
        //     addon_price: Number(addon.price),
        //     name: addon.label,
        //   }));

        // Get pet and service details for display
        const selectedPet = pets.find((pet) => pet._id === petService.pet_id);
        const selectedService = services.find(
          (service) => service._id === petService.service_id
        );

        // Create daycare data object for API payload
        const daycareData = {
          item_type: "day_care",
          vendor_pet_id: petService.pet_id,
          service_id: petService.service_id,
          start_date: petService.checkin_date,
          start_time: petService.start_time,
          duration: petService.duration,
          frequency: petService.frequency,
          quantity: 1, // Default quantity for daycare
          price: parseFloat(petService.price) || 0,
          unit_price: parseFloat(petService.price) || 0,
          discount: parseFloat(petService.discount) || 0,
          gst: parseFloat(petService.gst_amount) || 0,
          notes: petService.notes || "", // Add notes field as required by API
          total_price: parseFloat(petService.totalAmount) || 0,
          service_category_type_id: serviceTypeId,
          // addons: selectedAddons,
          addons: itemData?.addons || [],
          // Add display fields for AddAppointment component
          pet_name: selectedPet?.name || petService.pet_name || "",
          service_name: selectedService?.name || petService.service_name || "",
          service_type_name:
            selectedService?.service_category?.service_type_name ||
            petService.service_type_name ||
            "Day Care",
          service_type_id:
            selectedService?.service_category?.service_type_id ||
            petService.service_type_id ||
            "",
          pet_breed: selectedPet?.breed?.breed_name || "",
          pet_type: selectedPet?.pet_type?.name || "",
          pet_type_id: petDetails?.pet_type?._id || "",
          pet_size: selectedPet?.size?.size_label || "",
          tax_percentage: petService.tax_percentage || 0,
          // Add ref_id if available from itemData
          ...(itemData?.refId && { ref_id: itemData.refId }),
          ...(itemData?._id && { ref_id: itemData._id }), // Fallback to _id if refId not available
        };

        // If we are editing an existing locally-added daycare in AddAppointment (calendar flow)
        if (itemData?._editIndex !== undefined) {
          daycareData._editIndex = itemData._editIndex;
          if (itemData?._originalDaycare) {
            daycareData._originalDaycare = itemData._originalDaycare;
          }
        }

        // Check if this is being called from calendar
        if (fromCalendar) {
          // Check if this is an edit operation
          if (itemData?._editIndex !== undefined) {
            // This is an update to an existing appointment in the list
            const editIndex = itemData._editIndex;
            const updatedData = {
              ...daycareData,
              _editIndex: editIndex,
              _originalAppointment: itemData._originalAppointment,
            };
            onUpdate(updatedData);
          } else {
            // This is a new appointment being added
            onUpdate(daycareData);
          }
          resetFormData();
          onClose();
          return;
        }

        // Prepare payload for API (required for both create & update) - only for non-calendar calls
        let updatedFormData = {
          order_type: "offline",
          vendor_client_id: clientId,
          order_items: [
            {
              ...daycareData,
              ...(appointmentStatusRef?.current && {
                status: appointmentStatusRef.current,
              }),
            },
          ],
          ...(orderId && { order_id: orderId }),
          ...(itemData?.order_id && { order_id: itemData.order_id }),
          // Add ref_id at the top level if available
          ...(itemData?.refId && { ref_id: itemData.refId }),
          ...(itemData?._id && { ref_id: itemData._id }), // Fallback to _id if refId not available
        };

        try {
          const response = await httpService.post(
            "/vendororder/addVendorOrderOrUpdate",
            updatedFormData
          );
          if (onUpdate) onUpdate(response.data);

          if (!suppressToasts) {
            if (editMode) {
              toast.success("Daycare updated successfully!", {
                position: "top-center",
              });
            } else {
              toast.success("Daycare added successfully!", {
                position: "top-center",
              });
            }
          }

          resetFormData();
          onClose();
        } catch (error) {
          console.error("Error saving daycare:", error);
          toast.error("Failed to save daycare");
          return;
        }
      } catch (error) {
        console.error("Error adding daycare:", error);
        toast.error("Failed to add daycare. Please try again", {
          position: "top-right",
        });
      }
    };

    // Main component content
    const mainContent = (
      <>
        {showNewPetForm ? (
          <AddNewPet
            isOpen={showNewPetForm}
            onClose={() => setShowNewPetForm(false)}
            onSave={(addedPetData) => {
              setNewPet({
                type: addedPetData.pet_type,
                name: addedPetData.name,
                gender: addedPetData.gender,
                size: addedPetData.size,
                breed: addedPetData.breed,
                age: addedPetData.age,
                birth_date: addedPetData.birthday,
                behavior_notes: addedPetData.behavior_notes,
                tags: addedPetData.tags,
              });

              setPetService((prev) => ({
                ...prev,
                pet_id: addedPetData._id,
              }));
              fetchPetDetails(addedPetData._id);
              setPets((prevPets) => [...prevPets, addedPetData]);
            }}
            clientId={clientId}
            isFullWidth={true}
            shouldHideSaveAndAdd={true}
            petId={isEditPet ? petDetails._id : null}
          />
        ) : (
          <>
            <Box
              sx={{ height: "100vh", display: "flex", flexDirection: "column" }}
            >
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1000,
                  backgroundColor: "#fff",
                  px: 2,
                  py: 1.5,
                  borderBottom: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {!renderAsContent && (
                  <button
                    onClick={onClose}
                    className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors duration-200"
                  >
                    <FiArrowLeft className="w-5 h-5 text-gray-700" />
                  </button>
                )}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  px: 2,
                  pb: 10,
                }}
              >
                {/* Daycare form content */}
                <Grid container spacing={2}>
                  {/* Pet Selection */}
                  {!itemData?.ref_id && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={0.5}
                        className="flex items-center gap-2"
                      >
                        <PetsIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                        Pet <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Box display="flex" gap={1}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={petService.pet_id || ""}
                          onChange={(e) => {
                            if (isPetDisabled) return;
                            const petId = e.target.value;
                            setPetService((prev) => ({
                              ...prev,
                              pet_id: petId,
                            }));
                            if (petId) {
                              fetchPetDetails(petId);
                            }
                          }}
                          disabled={isPetDisabled}
                          SelectProps={{
                            displayEmpty: true,
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PetsIcon sx={{ color: isPetDisabled ? "#9ca3af" : "#6b7280", fontSize: 18 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            mb: 1,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: isPetDisabled ? "#f9fafb" : "white",
                              border: "1px solid #e5e7eb",
                              "&:hover": {
                                borderColor: isPetDisabled ? "#e5e7eb" : "#3b82f6",
                                boxShadow: isPetDisabled ? "none" : "0 4px 12px rgba(59, 130, 246, 0.15)",
                              },
                              "&.Mui-focused": {
                                borderColor: isPetDisabled ? "#e5e7eb" : "#3b82f6",
                                boxShadow: isPetDisabled ? "none" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                              },
                            },
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#374151",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            {isPetDisabled ? "Pet pre-selected" : "Select pet"}
                          </MenuItem>
                          {pets
                          .filter((pet) => pet.status?.toLowerCase() !== "inactive")
                          ?.map((pet) => (
                            <MenuItem key={pet._id} value={pet._id}>
                              {pet.name} {pet.breed?.breed_name ? `- ${pet.breed.breed_name}` : ""}
                              {pet.age ? ` (${pet.age} yrs)` : ""}
                            </MenuItem>
                          ))}
                        </TextField>
                          {!isPetDisabled && (
                            <>
                            {petDetails?._id && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setIsEditPet(true);
                                setShowNewPetForm(true);
                              }}
                              sx={{
                                minWidth: "60px",
                                height: "40px",
                                textTransform: "none",
                                borderRadius: "12px",
                                background:
                                  "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                                border: "none",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "13px",
                                boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                                  boxShadow:
                                    "0 6px 16px rgba(99, 102, 241, 0.35)",
                                  transform: "translateY(-1px)",
                                  border: "none",
                                },
                                "& .MuiButton-startIcon": {
                                  marginRight: "6px",
                                  "& svg": {
                                    fontSize: "16px",
                                  },
                                },
                              }}
                              style={{
                                display: isPetDisabled ? "none" : "inline-flex",
                              }}
                            >
                              Edit
                            </Button>
                          )}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {setIsEditPet(true);setShowNewPetForm(true);}}
                          sx={{
                            minWidth: "100px",
                            height: "40px",
                            textTransform: "none",
                            borderRadius: "12px",
                            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                            border: "none",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "13px",
                            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)",
                            "&:hover": {
                              background: "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                              boxShadow: "0 6px 16px rgba(99, 102, 241, 0.35)",
                              transform: "translateY(-1px)",
                              border: "none",
                            },
                            "& .MuiButton-startIcon": {
                              marginRight: "6px",
                              "& svg": {
                                fontSize: "16px",
                              },
                            },
                          }}
                          startIcon={<MdPets />}
                          style={{ display: isPetDisabled ? "none" : "inline-flex" }}
                        >
                          Add Pet
                        </Button>
                        </>
                          )}
                      </Box>
                    </Grid>
                  )}

                  {/* Pet Details Display */}
                  {petDetails.length != 0 && (
                    <Grid item xs={12}>
                      <PetCard pet={petDetails} onEdit={() => {
                      setIsEditPet(true);
                      setShowNewPetForm(true);
                    }}/>
                    </Grid>
                  )}

                  {/* Check-in Date and Time */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <FiCalendar className="w-4 h-4 text-blue-600" />
                      Check-in Date
                    </Typography>
                    <TextField
                      type="date"
                      lang="en-IN"
                      fullWidth
                      size="small"
                      value={petService.checkin_date}
                      onChange={(e) =>
                        handlePetServiceChange("checkin_date", e.target.value)
                      }
                      inputProps={{
                        min: minDateStr,
                      }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          "&:hover": {
                            borderColor: "#3b82f6",
                            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: "#3b82f6",
                            boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                          },
                        },
                      }}
                      placeholder="Select checkin date"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <FiClock className="w-4 h-4 text-green-600" />
                      Time
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={petService.start_time || ""}
                      onChange={(e) => {
                        handlePetServiceChange("start_time", e.target.value);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTimeIcon
                              sx={{ color: "#6b7280", fontSize: 18 }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          "&:hover": {
                            borderColor: "#10b981",
                            boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: "#10b981",
                            boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)",
                          },
                        },
                      }}
                      placeholder="Select time"
                    >
                      {timeSlots
                        .filter(
                          (slot) =>
                            !isPastTimeSlot(slot, petService.appointment_date)
                        )
                        .map((time) => (
                          <MenuItem key={time} value={time}>
                            {time}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>

                  {/* Frequency and Duration - Reordered */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <RepeatIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
                      Frequency <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={petService.frequency || ""}
                      onChange={(e) => {
                        const label = e.target.value;
                        const frequencyKey = mapFreqLabelToKey(label);
                        handlePetServiceChange("frequency", label);

                        // Build duration options based on selected frequency
                        buildDurationOptionsFromFrequency(frequencyKey);

                        // Clear duration and service when frequency changes
                        handlePetServiceChange("duration", "");
                        handlePetServiceChange("service_id", "");
                        setPetService((prev) => ({ ...prev, price: 0 }));
                        setServices([]); // Clear services until duration is selected
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <RepeatIcon
                              sx={{ color: "#6b7280", fontSize: 18 }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          "&:hover": {
                            borderColor: "#8b5cf6",
                            boxShadow: "0 4px 12px rgba(139, 92, 246, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: "#8b5cf6",
                            boxShadow: "0 0 0 3px rgba(139, 92, 246, 0.1)",
                          },
                        },
                      }}
                      placeholder="Select frequency"
                    >
                      {frequencyOptions && frequencyOptions.length > 0 ? (
                        frequencyOptions.map((opt) => (
                          <MenuItem key={opt.key} value={opt.label}>
                            {opt.label}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          Loading frequencies...
                        </MenuItem>
                      )}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <TimerIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                      Duration <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={petService.duration || ""}
                      onChange={(e) => {
                        const selectedDurationKey = e.target.value; // e.g. '1hr'
                        const selectedFrequencyKey = mapFreqLabelToKey(
                          petService.frequency || ""
                        );

                        handlePetServiceChange("duration", selectedDurationKey);

                        // Filter services based on frequency and duration
                        if (selectedFrequencyKey && selectedDurationKey) {
                          filterServicesBasedOnFrequencyAndDuration(
                            selectedFrequencyKey,
                            selectedDurationKey
                          );
                        }

                        // Clear service selection when duration changes
                        handlePetServiceChange("service_id", "");
                        setPetService((prev) => ({ ...prev, price: 0 }));
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TimerIcon
                              sx={{ color: "#6b7280", fontSize: 18 }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          "&:hover": {
                            borderColor: "#f59e0b",
                            boxShadow: "0 4px 12px rgba(245, 158, 11, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: "#f59e0b",
                            boxShadow: "0 0 0 3px rgba(245, 158, 11, 0.1)",
                          },
                        },
                      }}
                      placeholder="Select duration"
                    >
                      {durationOptions && durationOptions.length > 0 ? (
                        durationOptions.map((opt) => (
                          <MenuItem key={opt.key} value={opt.key}>
                            {opt.label}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          {petService.frequency
                            ? "No durations available for this frequency"
                            : "Select frequency first"}
                        </MenuItem>
                      )}
                    </TextField>
                  </Grid>

                  {/* Service Selection */}
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <MedicalServicesIcon
                        sx={{ fontSize: 16, color: "#10b981" }}
                      />
                      Service <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Box display="flex" gap={1}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={petService.service_id}
                        onChange={(e) => {
                          const serviceId = e.target.value;
                          setPetService((prev) => ({
                            ...prev,
                            service_id: serviceId,
                          }));

                          if (serviceId) {
                            fetchServiceDetails(serviceId).then(() => {
                              // After fetching service details, call price API if frequency and duration are set
                              const selectedFrequencyKey = mapFreqLabelToKey(
                                petService.frequency || ""
                              );
                              const selectedDurationKey =
                                petService.duration || "";

                              if (selectedFrequencyKey && selectedDurationKey) {
                                // Call price API instead of using local pricing
                                fetchPriceFromAPI(
                                  serviceId,
                                  selectedFrequencyKey,
                                  selectedDurationKey
                                );
                              }

                              // Update GST from selected service only if not already set from vendorbooking API
                              const selectedService = services.find(
                                (s) => s._id === serviceId
                              );
                              if (selectedService) {
                                setPetService((prev) => ({
                                  ...prev,
                                  // Preserve GST from vendorbooking API response, only set from service if not already set
                                  gst: selectedService.tax_percentage || 0,
                                  tax_percentage:
                                    prev.tax_percentage ||
                                    selectedService.tax_percentage ||
                                    0,
                                }));
                              }
                            });
                          } else {
                            setPetService((prev) => ({ ...prev, price: 0 }));
                            setServicesd({});
                          }
                        }}
                        renderValue={(value) => {
                          // Only show the service name in the text field, not the price
                          const selectedService = services.find(
                            (service) => service._id === value
                          );
                          return selectedService
                            ? selectedService.name
                            : "Select service";
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MedicalServicesIcon
                                sx={{ color: "#6b7280", fontSize: 18 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "white",
                            border: "1px solid #e5e7eb",
                            "&:hover": {
                              borderColor: "#10b981",
                              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)",
                            },
                            "&.Mui-focused": {
                              borderColor: "#10b981",
                              boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)",
                            },
                          },
                        }}
                      >
                        {services && services.length > 0 ? (
                          services.map((service) => {
                            // Get price based on selected frequency and duration
                            let servicePrice = 0;
                            const frequencyKey = mapFreqLabelToKey(
                              petService.frequency || ""
                            );
                            const durationKey = petService.duration || "";

                            if (
                              service.pricing &&
                              service.pricing[durationKey] &&
                              service.pricing[durationKey][frequencyKey]
                            ) {
                              servicePrice =
                                service.pricing[durationKey][frequencyKey];
                            }

                            return (
                              <MenuItem
                                key={service._id}
                                value={service._id}
                                sx={{
                                  py: 1,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <span>{service?.name}</span>
                                <span
                                  style={{
                                    color: "#10b981",
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    marginLeft: "16px",
                                  }}
                                >
                                  ₹{servicePrice}
                                </span>
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem value="" disabled>
                            {!petService.frequency
                              ? "Select frequency first"
                              : !petService.duration
                              ? "Select duration first"
                              : "No services available for this frequency and duration"}
                          </MenuItem>
                        )}
                      </TextField>
                    </Box>
                  </Grid>

                  {/* Price Display */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <MdCurrencyRupee
                        style={{ fontSize: 16, color: "#f59e0b" }}
                      />
                      Price
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1.5,
                        borderRadius: "12px",
                        backgroundColor: "#fef3c7",
                        border: "1px solid #fde68a",
                        minHeight: "40px",
                        position: "relative",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: petService.price ? "#374151" : "#92400e",
                          fontStyle: petService.price ? "normal" : "italic",
                          flex: 1,
                        }}
                      >
                        {petService.price
                          ? `₹ ${petService.price}`
                          : "Select service to see price"}
                      </Typography>
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          right: "8px",
                          transform: "translateY(-50%)",
                          fontSize: "12px",
                          color: "#9ca3af",
                          fontWeight: 500,
                          backgroundColor: "#fef3c7",
                          px: 1,
                          borderRadius: "4px",
                        }}
                      >
                        Auto
                      </Box>
                    </Box>
                  </Grid>

                  {/* Discount, GST and Total */}
                  <Grid container spacing={1} sx={{ mb: 1, ml: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={0.5}
                        className="flex items-center gap-2"
                      >
                        <ReceiptIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
                        Service Charges (%)
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1.5,
                          borderRadius: "12px",
                          backgroundColor: "#f0f9ff",
                          border: "1px solid #bfdbfe",
                          minHeight: "40px",
                          position: "relative",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: petService.tax_percentage
                              ? "#374151"
                              : "#0369a1",
                            fontStyle: petService.tax_percentage
                              ? "normal"
                              : "italic",
                            flex: 1,
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                          }}
                        >
                          <InputAdornment position="start">
                            <ReceiptIcon
                              sx={{ color: "#6b7280", fontSize: 18 }}
                            />
                          </InputAdornment>
                          {petService.tax_percentage
                            ? petService.tax_percentage
                            : 0}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={0.5}
                        className="flex items-center gap-2"
                      >
                        <MdCurrencyRupee
                          style={{ fontSize: 16, color: "#f59e0b" }}
                        />
                        Total Amount
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1.5,
                          borderRadius: "12px",
                          backgroundColor: "#f0f9ff",
                          border: "1px solid #bfdbfe",
                          minHeight: "40px",
                          position: "relative",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              !isNaN(petService.totalAmount) &&
                              petService.totalAmount
                                ? "#374151"
                                : "#0369a1",
                            fontStyle:
                              !isNaN(petService.totalAmount) &&
                              petService.totalAmount
                                ? "normal"
                                : "italic",
                            flex: 1,
                          }}
                        >
                          {!isNaN(petService.totalAmount) &&
                          petService.totalAmount
                            ? `₹ ${petService.totalAmount}`
                            : "Total will be calculated"}
                        </Typography>
                        <Box
                          sx={{
                            position: "absolute",
                            top: "50%",
                            right: "8px",
                            transform: "translateY(-50%)",
                            fontSize: "12px",
                            color: "#9ca3af",
                            fontWeight: 500,
                            backgroundColor: "#f0f9ff",
                            px: 1,
                            borderRadius: "4px",
                          }}
                        >
                          Auto
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <NotesIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                      Notes
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      value={petService.notes}
                      onChange={(e) =>
                        handlePetServiceChange("notes", e.target.value)
                      }
                      placeholder="Describe symptoms, concerns, or reason for the visit..."
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          "&:hover": {
                            borderColor: "#6b7280",
                            boxShadow: "0 4px 12px rgba(107, 114, 128, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: "#6b7280",
                            boxShadow: "0 0 0 3px rgba(107, 114, 128, 0.1)",
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: "#fff",
                  px: 2,
                  py: 1.5,
                  borderTop: "1px solid #eee",
                  boxShadow: "0 -2px 12px rgba(0,0,0,0.1)",
                  zIndex: 1000,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      color="secondary"
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        resetFormData();
                        onClose();
                      }}
                      sx={{
                        py: 1.4,
                        fontWeight: "bold",
                        fontSize: "16px",
                        borderRadius: "12px",
                        textTransform: "none",
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {editMode ? (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<SaveIcon />}
                        onClick={handleSaveOrders}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }}
                      >
                        Update
                      </Button>
                    ) : fromCalendar ? (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<AddIcon />}
                        onClick={handleSaveOrders}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }}
                      >
                        Add
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<SaveIcon />}
                        onClick={handleSaveOrders}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }}
                      >
                        Save
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </>
        )}
      </>
    );

    return renderAsContent ? (
      mainContent
    ) : (
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => {
          resetFormData();
          onClose();
        }}
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : "80vw",
            maxWidth: 500,
            p: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflowX: "hidden",
          },
        }}
      >
        {mainContent}
      </Drawer>
    );
  }
);

// Add display name for debugging
DaycareInlineForm.displayName = "DaycareInlineForm";

export default DaycareInlineForm;
